/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { motion } from "framer-motion";
import { ArrowUpRight, Check, MapPin, Sparkles } from "lucide-react";

const badges = [
  "Maquette sous 48h",
  "Un seul interlocuteur",
  "Visible sur Google",
  "Maintenance disponible",
];

export const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative isolate overflow-hidden pt-28 md:pt-32 pb-20 md:pb-24 bg-paper"
    >
      {/* Premium gradient background */}
      <div className="absolute inset-0 -z-10 grain" aria-hidden />
      <div
        className="absolute top-0 right-0 w-[820px] h-[820px] -z-10 opacity-90"
        style={{
          background:
            "radial-gradient(60% 60% at 70% 30%, rgba(6,182,212,0.14) 0%, transparent 60%), radial-gradient(50% 50% at 90% 0%, rgba(30,77,139,0.18) 0%, transparent 60%)",
        }}
        aria-hidden
      />
      <div
        className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full opacity-[0.5] -z-10 dot-grid"
        aria-hidden
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-cyan/30 text-ink-900 font-mono-label"
        >
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-cyan opacity-75 animate-pulse-ring" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan" />
          </span>
          <MapPin size={11} className="text-cyan-600" />
          Marssac-sur-Tarn — Tarn (81) — Occitanie
        </motion.div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-8">
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight text-ink"
            >
              Création de sites web{" "}
              <span className="italic font-light text-ink/80">performants</span>{" "}
              pour les entreprises du{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-ink via-ink-600 to-cyan-600 bg-clip-text text-transparent">Tarn</span>
                <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 200 10" preserveAspectRatio="none" aria-hidden>
                  <path d="M2 7 Q 50 2, 100 5 T 198 6" stroke="url(#g1)" strokeWidth="3" fill="none" strokeLinecap="round" />
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#16335E" />
                      <stop offset="100%" stopColor="#06B6D4" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
              .
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-8 max-w-2xl text-lg leading-relaxed text-ink/75"
            >
              Plus de visibilité. Plus de clients. Un site à la hauteur de
              votre entreprise — pensé pour convertir.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-5 max-w-2xl text-[15px] leading-relaxed text-ink/65 border-l-2 border-cyan pl-4"
            >
              <strong className="text-ink font-medium">Basé à Marssac-sur-Tarn</strong> — nous accompagnons
              PME, artisans et créateurs d'entreprise du Tarn.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <a
                href="#contact"
                data-testid="hero-cta-quote"
                className="group inline-flex items-center gap-3 h-14 px-7 btn-premium text-sm font-medium tracking-wide"
              >
                <Sparkles size={16} />
                Prendre un rendez-vous gratuit
                <ArrowUpRight size={18} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a
                href="#realisations"
                data-testid="hero-cta-call"
                className="inline-flex items-center gap-3 h-14 px-7 btn-ghost-premium text-sm font-medium"
              >
                Voir nos réalisations
              </a>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4"
            >
              {badges.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-ink/80">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-gradient-to-br from-cyan-400 to-ink-600 inline-flex items-center justify-center shrink-0">
                    <Check size={11} strokeWidth={3} className="text-white" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </motion.ul>
          </div>

          <motion.aside
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-4 lg:pl-8 lg:border-l lg:border-ink/10"
          >
            <p className="font-mono-label text-ink/60">Index</p>
            <ol className="mt-6 space-y-4 text-sm text-ink/80">
              {[
                "01 — Création & refonte de sites",
                "02 — Être trouvé sur Google",
                "03 — Page Google (fiche Maps)",
                "04 — Automatisations & IA",
              ].map((t) => (
                <li key={t} className="flex justify-between border-b border-ink/10 pb-3 group">
                  <span>{t}</span>
                  <span className="text-cyan-600 group-hover:translate-x-1 transition-transform">↗</span>
                </li>
              ))}
            </ol>
            <a
              href="#qui-suis-je"
              className="mt-8 inline-flex items-center gap-2 text-sm text-ink/70 hover:text-ink link-under"
              aria-label="Découvrir Marius Nougaillon, fondateur de WebUP"
            >
              → Qui suis-je ?
            </a>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};
